import React, { createContext, useContext, useEffect } from "react"

export type Locale = "tr" | "en"

type I18nContextType = {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string) => string
}

const I18nContext = createContext<I18nContextType | undefined>(undefined)

// Translation keys
const translations = {
  tr: {
    "nav.company": "Şirket",
    "nav.ourApproach": "Yaklaşımımız",
    "nav.products": "Ürünler",
    "nav.resources": "Kaynaklar",
    "nav.contact": "İletişim",
    "footer.quickLinks": "Hızlı Linkler",
    "footer.followUs": "Bizi Takip Edin",
    "footer.privacyPolicy": "Gizlilik Politikası",
    "footer.cookiePolicy": "Çerez Politikası",
    "contact.name": "Ad Soyad",
    "contact.email": "E-posta",
    "contact.phone": "Telefon",
    "contact.organization": "Organizasyon",
    "contact.subject": "Konu",
    "contact.message": "Mesaj",
    "contact.consent": "İletişim izni veriyorum",
    "contact.submit": "Gönder",
    "contact.success": "Mesajınız başarıyla gönderildi!",
    "contact.error": "Bir hata oluştu. Lütfen tekrar deneyin.",
    "contact.validation.nameRequired": "Ad soyad gereklidir",
    "contact.validation.nameMin": "Ad soyad en az 2 karakter olmalıdır",
    "contact.validation.emailRequired": "E-posta gereklidir",
    "contact.validation.emailInvalid": "Geçerli bir e-posta adresi giriniz",
    "contact.validation.phoneRequired": "Telefon numarası gereklidir",
    "contact.validation.phoneMin": "Telefon numarası en az 10 karakter olmalıdır",
    "contact.validation.subjectRequired": "Konu gereklidir",
    "contact.validation.subjectMin": "Konu en az 3 karakter olmalıdır",
    "contact.validation.messageRequired": "Mesaj gereklidir",
    "contact.validation.messageMin": "Mesaj en az 10 karakter olmalıdır",
    "contact.validation.consentRequired": "İletişim iznini onaylamalısınız",
    "product.status.research": "Araştırma",
    "product.status.pilot": "Pilot",
    "product.status.production": "Üretim",
    "product.downloads": "İndirilenler",
    "product.specifications": "Teknik Özellikler",
    "product.backToProducts": "Ürünlere Dön",
    "resource.type.blog": "Blog",
    "resource.type.press": "Basın Bülteni",
    "resource.type.publication": "Yayın",
    "resource.type.whitepaper": "Teknik Döküman",
    "resource.readMore": "Devamını Oku",
    "resource.backToResources": "Kaynaklara Dön",
    "common.learnMore": "Daha Fazla Bilgi",
    "common.loading": "Yükleniyor...",
    "common.error": "Hata",
  },
  en: {
    "nav.company": "Company",
    "nav.ourApproach": "Our Approach",
    "nav.products": "Products",
    "nav.resources": "Resources",
    "nav.contact": "Contact",
    "footer.quickLinks": "Quick Links",
    "footer.followUs": "Follow Us",
    "footer.privacyPolicy": "Privacy Policy",
    "footer.cookiePolicy": "Cookie Policy",
    "contact.name": "Full Name",
    "contact.email": "Email",
    "contact.phone": "Phone",
    "contact.organization": "Organization",
    "contact.subject": "Subject",
    "contact.message": "Message",
    "contact.consent": "I consent to being contacted",
    "contact.submit": "Submit",
    "contact.success": "Your message has been sent successfully!",
    "contact.error": "An error occurred. Please try again.",
    "contact.validation.nameRequired": "Name is required",
    "contact.validation.nameMin": "Name must be at least 2 characters",
    "contact.validation.emailRequired": "Email is required",
    "contact.validation.emailInvalid": "Please enter a valid email address",
    "contact.validation.phoneRequired": "Phone number is required",
    "contact.validation.phoneMin": "Phone number must be at least 10 characters",
    "contact.validation.subjectRequired": "Subject is required",
    "contact.validation.subjectMin": "Subject must be at least 3 characters",
    "contact.validation.messageRequired": "Message is required",
    "contact.validation.messageMin": "Message must be at least 10 characters",
    "contact.validation.consentRequired": "You must consent to being contacted",
    "product.status.research": "Research",
    "product.status.pilot": "Pilot",
    "product.status.production": "Production",
    "product.downloads": "Downloads",
    "product.specifications": "Specifications",
    "product.backToProducts": "Back to Products",
    "resource.type.blog": "Blog",
    "resource.type.press": "Press Release",
    "resource.type.publication": "Publication",
    "resource.type.whitepaper": "Whitepaper",
    "resource.readMore": "Read More",
    "resource.backToResources": "Back to Resources",
    "common.learnMore": "Learn More",
    "common.loading": "Loading...",
    "common.error": "Error",
  },
}

export const I18nProvider: React.FC<{
  children: React.ReactNode
  initialLang: Locale
}> = ({ children, initialLang }) => {
  const [locale, setLocale] = React.useState(initialLang)
  useEffect(() => {
    document.documentElement.lang = locale
    document.cookie = `lang=${locale}; path=/; max-age=${60 * 60 * 24 * 365}`
  }, [locale])

  const t = (key: string): string => {
    return translations[locale][key as keyof typeof translations.tr] ?? key
  }

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  )
}

export const useI18n = (): {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string) => string
} => {
  const context = useContext(I18nContext)
  if (typeof context === "undefined" || context === null) {
    throw new Error("useI18n must be used within I18nProvider")
  }
  return context
}

// Helper to get localized value from Sanity's localized fields
export function getLocalizedValue<T>(
  value: { tr: T; en: T } | undefined,
  locale: Locale,
  fallback: T
): T {
  if (typeof value === "undefined" || value === null) {
    return fallback
  }

  // Helper to check if a value is empty (undefined, null, empty string, or whitespace-only)
  const isEmpty = (val: T): boolean => {
    if (val === undefined || val === null) {
      return true
    }
    if (typeof val === "string") {
      return val.trim().length === 0
    }
    return false
  }

  // Try requested locale first
  const requestedValue = value[locale]
  if (!isEmpty(requestedValue)) {
    return requestedValue
  }

  // Try opposite locale as fallback
  const oppositeLocale = locale === "tr" ? "en" : "tr"
  const oppositeValue = value[oppositeLocale]
  if (!isEmpty(oppositeValue)) {
    return oppositeValue
  }

  // Use provided fallback
  return fallback
}
